import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Trash2, Plus, Send, Settings } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

interface WebhookManagerProps {
  serverId: string;
  serverName: string;
}

interface Webhook {
  id: string;
  url: string;
  secret: string;
  events: string[];
  is_active: boolean;
  created_at: string;
}

export const WebhookManager: React.FC<WebhookManagerProps> = ({ serverId, serverName }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [newWebhook, setNewWebhook] = useState({ url: '', secret: '', events: ['vote.created'] });
  const [testPayload, setTestPayload] = useState('');
  const [testingWebhookId, setTestingWebhookId] = useState<string | null>(null);

  const { data: webhooks, isLoading } = useQuery({
    queryKey: ['webhooks', serverId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('server_webhooks')
        .select('*')
        .eq('server_id', serverId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!serverId && !!user,
  });

  const createWebhookMutation = useMutation({
    mutationFn: async (webhook: { url: string; secret: string; events: string[] }) => {
      const { data, error } = await supabase
        .from('server_webhooks')
        .insert({
          server_id: serverId,
          url: webhook.url,
          secret: webhook.secret,
          events: webhook.events,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks', serverId] });
      setNewWebhook({ url: '', secret: '', events: ['vote.created'] });
      toast({
        title: t('webhookIntegration.webhookCreated'),
        description: t('webhookIntegration.webhookCreatedDesc'),
      });
    },
    onError: () => {
      toast({
        title: t('common.error'),
        description: t('webhookIntegration.testError'),
        variant: 'destructive',
      });
    },
  });

  const updateWebhookMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('server_webhooks')
        .update({ is_active })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks', serverId] });
      toast({
        title: t('webhookIntegration.webhookUpdated'),
        description: t('webhookIntegration.webhookUpdated'),
      });
    },
  });

  const deleteWebhookMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('server_webhooks')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks', serverId] });
      toast({
        title: t('webhookIntegration.webhookDeleted'),
        description: t('webhookIntegration.webhookDeleted'),
      });
    },
  });

  const testWebhookMutation = useMutation({
    mutationFn: async ({ webhookId }: { webhookId: string; payload?: string }) => {
      // Server signs a fixed safe test payload — user-supplied payloads are ignored server-side.
      const { data, error } = await supabase.functions.invoke('test-webhook', {
        body: { webhookId },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (result) => {
      toast({
        title: t('webhookIntegration.testSuccess'),
        description: `${t('common.status')}: ${result.status} ${result.statusText}`,
      });
      setTestingWebhookId(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur test webhook',
        description: error.message || 'Erreur lors du test du webhook',
        variant: 'destructive',
      });
      setTestingWebhookId(null);
    },
  });

  const generateSecret = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewWebhook({ ...newWebhook, secret: result });
  };

  const handleCreateWebhook = () => {
    if (!newWebhook.url || !newWebhook.secret) {
      toast({
        title: t('common.error'),
        description: t('webhookIntegration.urlRequired'),
        variant: 'destructive',
      });
      return;
    }
    createWebhookMutation.mutate(newWebhook);
  };

  if (isLoading) {
    return <div className="text-center p-4">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-3 overflow-x-hidden">
      <Card className="bg-slate-800 border-slate-700 overflow-hidden">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Settings className="w-4 h-4" />
            {t('webhookIntegration.title')} - {serverName}
          </CardTitle>
          <p className="text-xs text-slate-400 mt-0.5">{t('webhookIntegration.description')}</p>
        </CardHeader>
        <CardContent className="space-y-3 px-4 pb-4 pt-0">

          {/* Liste des webhooks existants */}
          {webhooks && webhooks.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-white text-sm">{t('webhookIntegration.configured')}</h4>
              {webhooks.map((webhook) => (
                <Card key={webhook.id} className="bg-slate-700 border-slate-600">
                  <CardContent className="p-2.5 sm:p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <code className="text-xs bg-slate-600 px-2 py-0.5 rounded text-slate-200 truncate block max-w-full overflow-hidden">
                          {webhook.url}
                        </code>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          {webhook.events.map((event) => (
                            <Badge key={event} variant="secondary" className="text-[10px] px-1.5 py-0">
                              {event}
                            </Badge>
                          ))}
                          <Badge variant={webhook.is_active ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0">
                            {webhook.is_active ? t('webhookIntegration.active') : t('webhookIntegration.inactive')}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Switch
                          checked={webhook.is_active}
                          onCheckedChange={(checked) =>
                            updateWebhookMutation.mutate({ id: webhook.id, is_active: checked })
                          }
                        />
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                setTestingWebhookId(webhook.id);
                                setTestPayload(JSON.stringify({
                                  type: 'vote.created',
                                  data: {
                                    server_id: serverId,
                                    vote_id: 'test-vote-id',
                                    player_name: 'TestPlayer',
                                    voted_at: new Date().toISOString(),
                                  },
                                }, null, 2));
                              }}
                            >
                              <Send className="w-3.5 h-3.5" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-slate-800 border-slate-700">
                            <DialogHeader>
                              <DialogTitle className="text-white">{t('webhookIntegration.testWebhook')}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <Label className="text-slate-300">{t('webhookIntegration.testPayloadLabel')}</Label>
                                <Textarea
                                  value={testPayload}
                                  onChange={(e) => setTestPayload(e.target.value)}
                                  className="bg-slate-700 border-slate-600 text-slate-200 font-mono text-sm"
                                  rows={10}
                                />
                              </div>
                              <Button
                                onClick={() => {
                                  if (testingWebhookId) {
                                    testWebhookMutation.mutate({
                                      webhookId: testingWebhookId,
                                      payload: testPayload,
                                    });
                                  }
                                }}
                                disabled={testWebhookMutation.isPending}
                                className="w-full"
                              >
                                {testWebhookMutation.isPending ? t('webhookIntegration.testing') : t('webhookIntegration.sendTest')}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => deleteWebhookMutation.mutate(webhook.id)}
                          disabled={deleteWebhookMutation.isPending}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <Separator className="bg-slate-600" />

          {/* Formulaire d'ajout */}
          <div className="space-y-2">
            <h4 className="font-medium text-white text-sm flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" />
              {t('webhookIntegration.createWebhook')}
            </h4>
            
            <div className="flex flex-col xl:flex-row gap-2 items-end">
              <div className="flex-1 min-w-0 w-full">
                <Label htmlFor="webhook-url" className="text-slate-300 text-xs">{t('webhookIntegration.urlLabel')} *</Label>
                <Input
                  id="webhook-url"
                  type="url"
                  placeholder={t('webhookIntegration.urlPlaceholder')}
                  value={newWebhook.url}
                  onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-slate-200 h-8 text-sm"
                />
              </div>
              <div className="flex-1 min-w-0 w-full">
                <Label htmlFor="webhook-secret" className="text-slate-300 text-xs">{t('webhookIntegration.secretLabel')} *</Label>
                <div className="flex gap-1.5">
                  <Input
                    id="webhook-secret"
                    type="text"
                    placeholder={t('webhookIntegration.secretPlaceholder')}
                    value={newWebhook.secret}
                    onChange={(e) => setNewWebhook({ ...newWebhook, secret: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-slate-200 flex-1 min-w-0 h-8 text-sm"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={generateSecret} className="shrink-0 h-8 text-xs">
                    {t('webhookIntegration.generateSecret')}
                  </Button>
                </div>
              </div>
            </div>

            <Button
              onClick={handleCreateWebhook}
              disabled={createWebhookMutation.isPending}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 h-8 text-xs"
            >
              {createWebhookMutation.isPending ? t('webhookIntegration.creating') : t('webhookIntegration.createWebhook')}
            </Button>
          </div>

          {/* Documentation compacte */}
          <Separator className="bg-slate-600" />
          <div className="space-y-1">
            <h4 className="font-medium text-white text-sm">{t('webhookIntegration.documentation')}</h4>
            <div className="text-xs text-slate-400 space-y-0.5">
              <p><strong>{t('webhookIntegration.supportedEvents')}</strong> <code className="text-[10px]">vote.created</code> – {t('webhookIntegration.voteCreated')}</p>
              <p><strong>{t('webhookIntegration.security')}</strong> {t('webhookIntegration.hmacSigned')} · <code className="text-[10px]">X-RS-Signature: sha256=…</code> · <code className="text-[10px]">X-RS-Event: …</code></p>
              <p><strong>{t('webhookIntegration.signatureFormat')}</strong> <code className="text-[10px] bg-slate-900 px-1.5 py-0.5 rounded">hash_hmac('sha256', $payload, $secret)</code></p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};